import React, { useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, Upload, X, Star, GripVertical, Check, AlertCircle, Loader2 } from "lucide-react";
import { adminAiGenerate, adminUpload, resolveImageUrl } from "../../lib/adminApi";
import { toast } from "sonner";

// ---------- Field wrapper ----------
export function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">
        {label} {required && <span className="text-[var(--wr-red)]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-[var(--wr-ink-2)]">{hint}</p>}
    </label>
  );
}

export const inputCls = "w-full border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2.5 focus:outline-none focus:border-[var(--wr-ink)] transition-colors";
export const textareaCls = inputCls + " resize-y min-h-[92px]";

// ---------- Autosave status pill ----------
export function AutosaveIndicator({ state }) {
  const map = {
    idle:    { icon: <Check size={12} />, label: "Saved", cls: "text-[var(--wr-ink-2)]" },
    saving:  { icon: <Loader2 size={12} className="animate-spin" />, label: "Saving…", cls: "text-[var(--wr-ink)]" },
    unsaved: { icon: <AlertCircle size={12} />, label: "Unsaved", cls: "text-[var(--wr-red)]" },
    offline: { icon: <AlertCircle size={12} />, label: "Offline — using local backup", cls: "text-[var(--wr-red)]" },
    saved:   { icon: <Check size={12} />, label: "Saved", cls: "text-emerald-700" },
  };
  const s = map[state] || map.idle;
  return (
    <span data-testid={`autosave-${state}`} className={`inline-flex items-center gap-1.5 font-mono-wr text-[10px] uppercase tracking-widest ${s.cls}`}>
      {s.icon} {s.label}
    </span>
  );
}

// ---------- AI Assist popover ----------
export function AiAssistButton({ field, locale, context, onInsert, testid }) {
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await adminAiGenerate({ field, locale, seed, ...context });
      setPreview(r.text || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally { setBusy(false); }
  };
  const insert = () => { onInsert(preview); setOpen(false); setPreview(""); setSeed(""); };

  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" onClick={()=>setOpen(v=>!v)} data-testid={testid}
        className="inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--wr-border)] bg-[var(--wr-surface)] hover:border-[var(--wr-ink)] transition-colors font-mono-wr text-[10px] uppercase tracking-widest">
        <Sparkles size={12} className="text-[var(--wr-red)]" /> Generate with AI
      </button>
      {open && (
        <div className="absolute z-30 right-0 mt-2 w-[360px] border border-[var(--wr-ink)] bg-[var(--wr-bg)] shadow-xl p-4 space-y-3" data-testid={`${testid}-popover`}>
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-red)]">AI · {locale.toUpperCase()} · {field}</p>
          <textarea value={seed} onChange={(e)=>setSeed(e.target.value)} rows={2}
            placeholder="A short hint, e.g. 'traditional guesthouse with wood-fired sauna'"
            className={textareaCls} data-testid={`${testid}-seed`} />
          <button type="button" onClick={run} disabled={busy}
            className="w-full px-3 py-2 bg-[var(--wr-ink)] text-[var(--wr-bg)] font-mono-wr text-[10px] uppercase tracking-widest hover:bg-[var(--wr-red)] disabled:opacity-50"
            data-testid={`${testid}-run`}>
            {busy ? "Generating…" : "Generate"}
          </button>
          {preview && (
            <>
              <div className="border border-[var(--wr-border)] bg-[var(--wr-surface)] p-3 text-[13px] leading-relaxed max-h-52 overflow-auto whitespace-pre-wrap" data-testid={`${testid}-preview`}>
                {preview}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={insert} className="flex-1 px-3 py-2 bg-[var(--wr-ink)] text-[var(--wr-bg)] font-mono-wr text-[10px] uppercase tracking-widest hover:bg-[var(--wr-red)]" data-testid={`${testid}-insert`}>
                  Insert
                </button>
                <button type="button" onClick={run} className="px-3 py-2 border border-[var(--wr-border)] font-mono-wr text-[10px] uppercase tracking-widest">Retry</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Image uploader with drag / drop / reorder / captions ----------
export function ImageUploader({ heroUrl, gallery, captions, onHeroChange, onGalleryChange, onCaptionsChange }) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(0); // number in flight
  const [urlInput, setUrlInput] = useState("");
  const dragIdx = useRef(null);
  const inputRef = useRef(null);
  const heroInGallery = !!heroUrl && (gallery || []).includes(heroUrl);
  const addHeroToGallery = () => {
    if (!heroUrl || heroInGallery) return;
    onGalleryChange([heroUrl, ...(gallery || [])]);
    onCaptionsChange(["", ...(captions || [])]);
  };
  const clearHero = () => onHeroChange("");

  const uploadFiles = useCallback(async (files) => {
    const arr = Array.from(files || []).filter(f => /^image\/(jpeg|png|webp)$/.test(f.type));
    if (arr.length === 0) return;
    setUploading(arr.length);
    const newImages = [];
    for (const f of arr) {
      try {
        const r = await adminUpload(f);
        newImages.push(r.url);
      } catch (e) {
        toast.error(`Upload failed: ${f.name}`);
      }
      setUploading(n => n - 1);
    }
    const merged = [...gallery, ...newImages];
    onGalleryChange(merged);
    onCaptionsChange([...captions, ...newImages.map(()=>"")]);
    if (!heroUrl && newImages.length > 0) onHeroChange(newImages[0]);
  }, [gallery, captions, heroUrl, onGalleryChange, onCaptionsChange, onHeroChange]);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    uploadFiles(e.dataTransfer?.files);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };

  const remove = (i) => {
    const next = gallery.filter((_,j)=>j!==i);
    const nextCaps = captions.filter((_,j)=>j!==i);
    onGalleryChange(next); onCaptionsChange(nextCaps);
    if (heroUrl === gallery[i]) onHeroChange(next[0] || "");
  };
  const setHero = (u) => onHeroChange(u);
  const setCaption = (i, v) => {
    const next = captions.slice();
    while (next.length < gallery.length) next.push("");
    next[i] = v;
    onCaptionsChange(next);
  };
  const onCardDragStart = (i) => (e) => { dragIdx.current = i; e.dataTransfer.effectAllowed = "move"; };
  const onCardDragOver = (i) => (e) => { e.preventDefault(); };
  const onCardDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const next = gallery.slice();
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    const nextCaps = captions.slice();
    while (nextCaps.length < gallery.length) nextCaps.push("");
    const [movedCap] = nextCaps.splice(from, 1);
    nextCaps.splice(i, 0, movedCap);
    dragIdx.current = null;
    onGalleryChange(next); onCaptionsChange(nextCaps);
  };
  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    onGalleryChange([...gallery, u]);
    onCaptionsChange([...captions, ""]);
    if (!heroUrl) onHeroChange(u);
    setUrlInput("");
  };

  return (
    <div className="space-y-4" data-testid="image-uploader">
      {/* Current hero — always visible so the owner knows exactly what the public site renders */}
      <div className="border border-[var(--wr-border)] bg-[var(--wr-surface)] p-4" data-testid="current-hero-panel">
        <div className="flex items-start gap-4">
          <div className="w-28 h-20 shrink-0 bg-[var(--wr-surface-2)] border border-[var(--wr-border)] overflow-hidden">
            {heroUrl ? (
              <img src={resolveImageUrl(heroUrl)} alt="" className="w-full h-full object-cover" data-testid="current-hero-image" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[var(--wr-ink-2)] text-[10px] font-mono-wr uppercase">no hero</div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-red)] mb-1">Current hero (public banner)</p>
            {heroUrl ? (
              <>
                <p className="text-[13px] break-all text-[var(--wr-ink)]" data-testid="current-hero-url">{heroUrl}</p>
                {!heroInGallery && (
                  <div className="mt-2 flex items-center gap-2" data-testid="hero-not-in-gallery-warning">
                    <AlertCircle size={12} className="text-[var(--wr-red)] shrink-0" />
                    <p className="text-[12px] text-[var(--wr-red)]">
                      Hero image is not in your gallery. Add it, or star a gallery image to replace it.
                    </p>
                    <button type="button" onClick={addHeroToGallery} className="ml-auto px-2 py-1 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest" data-testid="add-hero-to-gallery">Add to gallery</button>
                    <button type="button" onClick={clearHero} className="px-2 py-1 border border-[var(--wr-border)] hover:border-[var(--wr-red)] hover:text-[var(--wr-red)] font-mono-wr text-[10px] uppercase tracking-widest" data-testid="clear-hero">Clear</button>
                  </div>
                )}
              </>
            ) : (
              <p className="text-[13px] text-[var(--wr-ink-2)]">No hero yet. Upload an image below and star it, or paste a URL.</p>
            )}
          </div>
        </div>
      </div>

      <div
        onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${dragActive ? "border-[var(--wr-red)] bg-[var(--wr-surface-2)]" : "border-[var(--wr-border)] bg-[var(--wr-surface)] hover:border-[var(--wr-ink)]"}`}
        data-testid="drop-zone"
      >
        <Upload size={22} className="mx-auto text-[var(--wr-ink-2)]" />
        <p className="mt-2 font-mono-wr text-[11px] uppercase tracking-widest">
          {dragActive ? "Drop to upload" : "Drop images here or click to browse"}
        </p>
        <p className="mt-1 text-[11px] text-[var(--wr-ink-2)]">JPG · PNG · WEBP · up to 10 MB each</p>
        {uploading > 0 && <p className="mt-2 font-mono-wr text-[10px] text-[var(--wr-red)]">Uploading {uploading}…</p>}
        <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp"
          className="hidden" onChange={(e)=>uploadFiles(e.target.files)} data-testid="file-input" />
      </div>

      <div className="flex gap-2">
        <input value={urlInput} onChange={(e)=>setUrlInput(e.target.value)}
          placeholder="…or paste an image URL"
          className={inputCls + " flex-1"} data-testid="image-url-input" />
        <button type="button" onClick={addUrl}
          className="px-4 py-2 border border-[var(--wr-border)] font-mono-wr text-[10px] uppercase tracking-widest hover:border-[var(--wr-ink)]"
          data-testid="add-image-url">Add</button>
      </div>

      {gallery.length > 0 && (
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="gallery-grid">
          {gallery.map((u, i) => (
            <li key={u+i}
              draggable onDragStart={onCardDragStart(i)} onDragOver={onCardDragOver(i)} onDrop={onCardDrop(i)}
              className="border border-[var(--wr-border)] bg-[var(--wr-surface)] relative group"
              data-testid={`gallery-item-${i}`}>
              <div className="relative aspect-[4/3] bg-[var(--wr-surface-2)]">
                <img src={resolveImageUrl(u)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={()=>remove(i)}
                  className="absolute top-1 right-1 w-7 h-7 flex items-center justify-center bg-black/60 text-white hover:bg-[var(--wr-red)]"
                  aria-label="Remove image" data-testid={`gallery-remove-${i}`}>
                  <X size={14} />
                </button>
                <button type="button" onClick={()=>setHero(u)}
                  className={`absolute top-1 left-1 w-7 h-7 flex items-center justify-center ${heroUrl===u?"bg-[var(--wr-red)] text-white":"bg-black/60 text-white hover:bg-[var(--wr-red)]"}`}
                  aria-label="Set as hero" data-testid={`gallery-hero-${i}`} title={heroUrl===u?"Hero image":"Set as hero"}>
                  <Star size={14} fill={heroUrl===u?"currentColor":"none"} />
                </button>
                <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 text-white font-mono-wr text-[9px]">
                  <GripVertical size={10} className="inline mr-1" />drag
                </span>
              </div>
              <input value={captions[i] || ""} onChange={(e)=>setCaption(i, e.target.value)}
                placeholder="Caption (optional)"
                className="w-full border-t border-[var(--wr-border)] px-2 py-1.5 text-[12px] bg-white"
                data-testid={`gallery-caption-${i}`} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
